/**
 * PersonaQuestionnaire — iter71 (Item 2)
 *
 * Three-question modal shown once per case to collect a self-reported persona.
 * The Dashboard uses the answers to reorder / reframe product sections
 * (Compass, Manifest, Roam, Smart Packing) via progressive disclosure.
 *
 * Honest-UX rules (matches OnboardingWalkthrough):
 *  - Skip is fully equal-weight to Continue. Skipping stores `persona_skipped: true`
 *    so we never re-prompt. Users can still edit later via the dashboard footer.
 *  - Backdrop click and Esc close the modal and count as SKIP (same path).
 *  - No urgency copy, no dark patterns, no "recommended" nudge on any option.
 *  - Portaled to document.body so the Dashboard's transformed <main> ancestor
 *    can't collapse `position: fixed` into `position: absolute`.
 *
 * The three dimensions (kept intentionally short — 3 questions, one screen):
 *  1. traveler_type   — budget-conscious | comfort-focused
 *  2. primary_purpose — academic | cultural | career | travel
 *  3. budget_tier     — low (€) | mid (€€) | high (€€€)
 */
import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { X } from "lucide-react";

const TRAVELER_OPTIONS = [
  { value: "budget",  label: "Budget-conscious", hint: "Every euro matters. I'll optimise for cost." },
  { value: "comfort", label: "Comfort-focused", hint: "I'll pay more for ease, quality, and time saved." },
];

const PURPOSE_OPTIONS = [
  { value: "academic", label: "Academic focus",     hint: "Coursework, credit, grades come first." },
  { value: "cultural", label: "Cultural immersion", hint: "Language, food, everyday local life." },
  { value: "career",   label: "Career / networking", hint: "Firms, alumni, internships, referrals." },
  { value: "travel",   label: "Travel-focused",      hint: "Weekend trips and seeing as much of Europe as I can." },
];

const BUDGET_OPTIONS = [
  { value: "low",  label: "€",   hint: "Tight budget. Hostels, buses, cook at home most days." },
  { value: "mid",  label: "€€",  hint: "Mid-range. Occasional flights and restaurants, mostly frugal." },
  { value: "high", label: "€€€", hint: "Comfortable budget. Flights and dining are fine when it saves time." },
];

// iter75 (Item A) — 4th question folds Manifest opt-in into the persona flow.
// Copy is deliberately written as a real choice, not a checkbox, so the two
// options carry equal visual weight and the user can honestly answer either.
// Never silently auto-opts anyone in: Yes triggers the manifest_profile
// upsert; No leaves opt_in_matching at false; skipping the whole modal also
// leaves opt_in_matching at false. Users can still opt in later from the
// Manifest Matches tab (or from the Roam gate CTA shipped in iter73).
const VISIBILITY_OPTIONS = [
  { value: true,  label: "Yes, opt me in", hint: "Peers heading to the same country see your first name and host city. You can create Trip Assemblies and receive requests." },
  { value: false, label: "Not right now",  hint: "Stay invisible for now. Cohort matching and trip coordination will be locked. You can turn this on anytime from Manifest." },
];

export default function PersonaQuestionnaire({ open, onClose, initialPersona = null }) {
  const [traveler, setTraveler]     = useState(initialPersona?.traveler_type   || null);
  const [purpose,  setPurpose]      = useState(initialPersona?.primary_purpose || null);
  const [budget,   setBudget]       = useState(initialPersona?.budget_tier     || null);
  // iter75 (Item A) — 4th question: Manifest visibility. Stored as tri-state
  // (null | true | false) so we can distinguish "not yet answered" from
  // "answered as No". Continue is disabled until visibility is a boolean.
  const [visibility, setVisibility] = useState(
    initialPersona?.cohort_visible === true || initialPersona?.cohort_visible === false
      ? initialPersona.cohort_visible
      : null
  );
  const [saving,   setSaving]       = useState(false);
  const [error,    setError]        = useState(null);

  // Reset local state each time the modal opens. Matches OnboardingWalkthrough.
  useEffect(() => {
    if (open) {
      setTraveler(initialPersona?.traveler_type   || null);
      setPurpose(initialPersona?.primary_purpose  || null);
      setBudget(initialPersona?.budget_tier       || null);
      setVisibility(
        initialPersona?.cohort_visible === true || initialPersona?.cohort_visible === false
          ? initialPersona.cohort_visible
          : null
      );
      setError(null);
    }
  }, [open, initialPersona]);

  // Body scroll-lock while modal is open (matches OnboardingWalkthrough).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const skip = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.patch("/cases/me/persona", { skipped: true });
    } catch (_) {
      // Silent fail — we don't want a network hiccup to trap the user in the modal.
    } finally {
      setSaving(false);
      onClose({ skipped: true, persona: null });
    }
  }, [saving, onClose]);

  const submit = useCallback(async () => {
    if (saving) return;
    // iter75 — all four answers required to Continue. Visibility must be a
    // real boolean (`null` means unanswered).
    if (!traveler || !purpose || !budget || visibility === null) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.patch("/cases/me/persona", {
        traveler_type:   traveler,
        primary_purpose: purpose,
        budget_tier:     budget,
        cohort_visible:  visibility,
      });
      onClose({ skipped: false, persona: res.data?.persona || null });
    } catch (err) {
      setError("Couldn't save right now. You can skip and come back to this later from the dashboard.");
    } finally {
      setSaving(false);
    }
  }, [saving, traveler, purpose, budget, visibility, onClose]);

  // Esc + backdrop close both count as skip (identical UX to onboarding modal).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") skip();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, skip]);

  if (!open) return null;

  const canSubmit = !!(traveler && purpose && budget) && visibility !== null && !saving;

  return createPortal(
    <div
      data-testid="persona-questionnaire"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 animate-[fadeIn_150ms_ease-out]"
      style={{ backgroundColor: "rgb(10, 10, 10)" }}
      onClick={skip}
    >
      <div
        className="w-full max-w-2xl bg-paper rounded-2xl shadow-2xl border border-ink/10 overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="persona-title"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-ink/10 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">
              Personalise your dashboard
            </div>
            <h2 id="persona-title" className="mt-1 text-xl md:text-2xl font-bold tracking-tight text-ink">
              Four quick questions.
            </h2>
            <p className="mt-1 text-[13px] text-muted leading-snug">
              Your answers reorder your dashboard so the most relevant things surface first, and set your cohort visibility. Nothing is shared with peers unless you choose Yes on the last question. Skip anytime.
            </p>
          </div>
          <button
            type="button"
            data-testid="persona-close"
            onClick={skip}
            aria-label="Close"
            className="shrink-0 w-9 h-9 rounded-full hover:bg-ink/5 flex items-center justify-center text-ink/60 hover:text-ink"
          >
            <X size={18} strokeWidth={2}/>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <QuestionBlock
            testid="persona-q-traveler"
            step={1}
            totalSteps={4}
            question="How do you think about spending on this exchange?"
            options={TRAVELER_OPTIONS}
            value={traveler}
            onChange={setTraveler}
          />
          <QuestionBlock
            testid="persona-q-purpose"
            step={2}
            totalSteps={4}
            question="What's your primary reason for the exchange?"
            options={PURPOSE_OPTIONS}
            value={purpose}
            onChange={setPurpose}
          />
          <QuestionBlock
            testid="persona-q-budget"
            step={3}
            totalSteps={4}
            question="Roughly, what monthly budget tier fits you best?"
            options={BUDGET_OPTIONS}
            value={budget}
            onChange={setBudget}
            layout="row"
          />
          {/* iter75 (Item A) — 4th question: Manifest visibility. Real choice,
             not a buried checkbox: Yes and Not-right-now render as equal-weight
             option cards, identical to Q1-Q3. Answer stored inside the persona
             blob and, if Yes, upserts a manifest_profile with
             opt_in_matching=true server-side. */}
          <QuestionBlock
            testid="persona-q-visibility"
            step={4}
            totalSteps={4}
            question="Do you want to be visible to your cohort for matching and trip coordination?"
            options={VISIBILITY_OPTIONS}
            value={visibility}
            onChange={setVisibility}
          />
          {error && (
            <div data-testid="persona-error" className="text-sm text-[color:var(--moe-pink)]">
              {error}
            </div>
          )}
        </div>

        {/* Footer. Skip and Continue have identical visual weight. */}
        <div className="px-6 py-4 border-t border-ink/10 flex items-center justify-between gap-3">
          <button
            type="button"
            data-testid="persona-skip"
            onClick={skip}
            disabled={saving}
            className="px-4 h-10 rounded-full border border-ink text-ink text-sm font-semibold hover:bg-ink/5 disabled:opacity-50"
          >
            Skip
          </button>
          <button
            type="button"
            data-testid="persona-continue"
            onClick={submit}
            disabled={!canSubmit}
            className="px-5 h-10 rounded-full bg-ink text-white text-sm font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : "Continue"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function QuestionBlock({ testid, step, totalSteps = 3, question, options, value, onChange, layout = "grid" }) {
  const gridClass = layout === "row"
    ? "grid grid-cols-3 gap-3"
    : "grid sm:grid-cols-2 gap-3";
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">
        Question {step} of {totalSteps}
      </div>
      <div className="mt-1 text-base md:text-lg font-bold text-ink leading-snug">
        {question}
      </div>
      <div className={`mt-3 ${gridClass}`}>
        {options.map((opt) => {
          const selected = value === opt.value;
          // iter75 — option keys must be strings for React's `key` prop; the
          // visibility question uses boolean values (true/false), so coerce
          // via String() rather than relying on primitive-string behaviour.
          const keyStr    = String(opt.value);
          const testidVal = typeof opt.value === "boolean" ? (opt.value ? "yes" : "no") : opt.value;
          return (
            <button
              key={keyStr}
              type="button"
              data-testid={`${testid}-${testidVal}`}
              aria-pressed={selected}
              onClick={() => onChange(opt.value)}
              className={`text-left rounded-xl border p-3 transition-colors ${
                selected
                  ? "border-elec bg-elec/5 ring-2 ring-elec/60"
                  : "border-ink/15 hover:border-ink/30 bg-white"
              }`}
            >
              <div className={`text-sm font-bold ${selected ? "text-elec" : "text-ink"}`}>
                {opt.label}
              </div>
              {opt.hint && (
                <div className="mt-1 text-[12px] text-ink/70 leading-snug">
                  {opt.hint}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
